import {useCallback, useEffect, useMemo} from 'react';

import type {Widget} from 'shells/widgets/types';

import {
    mapClientWidgetToGraphQLWidget,
    mapGraphQLWidgetToClientWidget,
    mapHeaderFieldFragmentToHeaderFieldInput,
} from '../../../core-entity/layout-customizer/helpers';
import {LayoutCustomizer} from '../../../core-entity/layout-customizer/layout-customizer';
import type {UpdateWidgetLayoutInput} from '../../../core-entity/layout-customizer/layout-customizer';

import {analyticsClient} from '../../../../analytics-client';

import {useGetPeepWidgets} from '../../graphql/hooks/use-get-peep-widgets';
import {useUpdatePeepWidgets} from '../../graphql/hooks/use-update-peep-widgets';

export const AccountLayoutCustomizer = () => {
    const {widgets, headerFields, isLoading} = useGetPeepWidgets('ACCOUNT');
    const {updatePeepWidgets, isLoading: isSaving} = useUpdatePeepWidgets();

    const sidebarWidgets = useMemo<Widget[]>(
        () => (widgets?.sidebarWidgets ?? []).map(mapGraphQLWidgetToClientWidget),
        [widgets?.sidebarWidgets]
    );
    const workspaceWidgets = useMemo<Widget[]>(
        () => (widgets?.workspaceWidgets ?? []).map(mapGraphQLWidgetToClientWidget),
        [widgets?.workspaceWidgets]
    );

    const handleSave = useCallback(
        (input: UpdateWidgetLayoutInput) => {
            // TODO: remove this once we fully strip out header customization
            // from the header
            const headerFieldInputs = NutClientConfig.hasCoreEntityFullHeightSidebarFF
                ? headerFields.map((field) => mapHeaderFieldFragmentToHeaderFieldInput(field))
                : input.headerFields.map((field) =>
                      mapHeaderFieldFragmentToHeaderFieldInput(field)
                  );

            updatePeepWidgets({
                entityType: 'ACCOUNT',
                sidebar: input.sidebar.map((w) => mapClientWidgetToGraphQLWidget(w)),
                workspace: input.workspace.map((w) => mapClientWidgetToGraphQLWidget(w)),
                headerFields: headerFieldInputs,
            });
        },
        [headerFields, updatePeepWidgets]
    );

    // Track an analytics event when a user views the layout customizer
    useEffect(() => {
        analyticsClient.track(
            analyticsClient.EVENTS.TRACK_PEEP_PAGES_REDESIGN_LAYOUT_CUSTOMIZER_VIEWED,
            {
                entityName: 'account',
            }
        );
    }, []);

    return (
        <>
            <LayoutCustomizer
                initialSidebarWidgets={sidebarWidgets}
                initialWorkspaceWidgets={workspaceWidgets}
                initialHeaderFields={
                    NutClientConfig.hasCoreEntityFullHeightSidebarFF ? undefined : headerFields
                }
                updateWidgetLayout={handleSave}
                isSaving={isSaving}
                isLoading={isLoading}
                title='Customizing company layout'
                cueCardProps={{
                    title: 'People and company layout customization',
                    description:
                        'Choose the content that matters most to you by enabling widgets, making it easier to review people and companies.',
                    illustrationString: 'peeps-onboarding',
                }}
                entityType='ACCOUNT'
            />
        </>
    );
};
